import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Box, IconButton, Typography, FormControl, LinearProgress } from "@mui/material";

import { Media } from "../../types/types/common.types";
import { formatImgUrl } from "../../utils/cardFormating";
import { MediaUploadError } from "../../api/v1/types/media.types";
import { useMultipleMediaUpload } from "../../api/v1/mutations/useMediaUpload";
import { formatMediaResponse } from "../../api/v1/formatters/media.formatters";
import { MediaFileIcon, MediaUploadFieldIcon, RemoveMediaButtonIcon } from "../../assets/icons/MUI/UIComponentsIcons";
import { formatFileSize } from "../../utils/formatters/formatFileSize";

interface MediaUploadFieldProps {
  value?: Media[] | Media | null;
  onChange: (value: Media[] | Media) => void;
  error?: string;
  multiple?: boolean;
  onRemove?: (media: Media) => void;
}

interface UploadingMedia {
  type: "IMAGE" | "VIDEO";
  file: File;
  thumbnail?: string;
  progress: number;
  fileName: string;
}

const MediaPreview = ({ item, onRemove }: { item: Media; onRemove: () => void }) => {
  const { type, url, thumbnail } = item;
  return (
    <Box sx={styles.previewItem}>
      <Box component="img" src={formatImgUrl(type === "IMAGE" ? url : thumbnail)} alt="" sx={styles.media} />
      <IconButton size="small" onClick={onRemove} sx={styles.removeButton}>
        <RemoveMediaButtonIcon />
      </IconButton>
    </Box>
  );
};

const UploadingMediaPreview = ({ item, onRemove }: { item: UploadingMedia; onRemove: () => void }) => {
  const { i18n } = useTranslation();

  return (
    <Box sx={styles.uploadingContainer}>
      <Box sx={styles.fileTypeContainer}>
        <Typography sx={styles.fileType}>{item.fileName.split(".")[1]}</Typography>
        <MediaFileIcon />
      </Box>
      <Box sx={styles.fileNameContainer}>
        <Typography sx={styles.fileName}>{item.fileName}</Typography>
        {!!item.file.size && (
          <Typography sx={styles.fileSize}>{formatFileSize(item.file.size, i18n.language)}</Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <LinearProgress variant="determinate" value={item.progress} sx={{ flex: 1 }} />
          {item.progress.toFixed(0)}%
        </Box>
      </Box>
      <IconButton size="small" onClick={onRemove} sx={styles.removeButton}>
        <RemoveMediaButtonIcon />
      </IconButton>
    </Box>
  );
};

export const MediaUploadField = ({
  value = [],
  onChange,
  error,
  multiple = false,
  onRemove,
}: MediaUploadFieldProps) => {
  const mediaArray = Array.isArray(value) ? value : value ? [value] : [];
  const { t } = useTranslation("common", { keyPrefix: "sharedComponents.mediaUploadField" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadingMedia, setUploadingMedia] = useState<UploadingMedia[]>([]);
  const [uploadError, setUploadError] = useState<MediaUploadError | null>(null);

  const { mutate: uploadMedia, isPending } = useMultipleMediaUpload();

  const handleFiles = async (files: File[]) => {
    setUploadError(null);
    const mediaFiles = files.filter((file) => file.type.startsWith("image/") || file.type.startsWith("video/"));

    if (mediaFiles.length === 0) {
      setUploadError({
        code: "INVALID_FILE_TYPE",
        message: "Please upload only image or video files",
      });
      return;
    }

    const uploadQueue: { file: File; type: "IMAGE" | "VIDEO"; thumbnailFile?: File; duration?: number }[] = [];

    for (const file of mediaFiles) {
      if (file.type.startsWith("video/")) {
        try {
          const metadata = await getVideoMetadata(file);
          uploadQueue.push({
            file,
            type: "VIDEO",
            thumbnailFile: metadata.thumbnail,
            duration: metadata.duration,
          });
        } catch (error) {
          console.error("Failed to get video metadata:", error);
          uploadQueue.push({ file, type: "VIDEO" });
        }
      } else {
        uploadQueue.push({ file, type: "IMAGE" });
      }
    }

    const newUploadingMedia = uploadQueue.map(({ file, type }) => ({
      type,
      file,
      thumbnail: type === "VIDEO" ? URL.createObjectURL(file) : undefined,
      progress: 0,
      fileName: file.name,
    }));

    setUploadingMedia((prev) => [...prev, ...newUploadingMedia]);

    const filesToUpload = uploadQueue.flatMap(({ file, thumbnailFile }) =>
      thumbnailFile ? [file, thumbnailFile] : [file]
    );

    uploadMedia(
      {
        files: filesToUpload,
        onProgress: (progress) => {
          setUploadingMedia((prev) =>
            prev.map((item) => {
              const fileProgress = progress.find((p) => p.fileName === item.fileName);
              return fileProgress ? { ...item, progress: fileProgress.progress } : item;
            })
          );
        },
      },
      {
        onSuccess: (responses) => {
          const formattedResponses = responses.map(formatMediaResponse);

          let currentIndex = 0;
          const newMedia = uploadQueue.map(({ type, duration }) => {
            if (type === "VIDEO") {
              const videoResponse = formattedResponses[currentIndex];
              const thumbnailResponse = formattedResponses[currentIndex + 1];
              currentIndex += 2;
              return {
                type: "VIDEO" as const,
                url: videoResponse.fileName,
                thumbnail: thumbnailResponse.fileName,
                duration: duration, // Use the extracted duration
              };
            } else {
              const imageResponse = formattedResponses[currentIndex];
              currentIndex += 1;
              return {
                type: "IMAGE" as const,
                url: imageResponse.fileName,
              };
            }
          });

          const updatedMedia = multiple ? [...mediaArray, ...newMedia] : newMedia[0];
          onChange(updatedMedia);
          setUploadingMedia([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        },
        onError: (error: MediaUploadError) => {
          setUploadError(error);
          setUploadingMedia([]);
        },
      }
    );
  };

  return (
    <FormControl fullWidth error={!!error}>
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept="image/*,video/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (!multiple && files.length > 1) {
            setUploadError({
              code: "MULTIPLE_FILES_NOT_ALLOWED",
              message: "Only one file can be uploaded at a time",
            });
            return;
          }
          handleFiles(files);
        }}
        disabled={isPending}
      />

      {!uploadingMedia.length && !mediaArray.length && (
        <Box sx={styles.dropZone} onClick={() => !isPending && fileInputRef.current?.click()}>
          <MediaUploadFieldIcon />
          <Box>
            <Typography sx={{ ...styles.dropZoneText, display: "flex", gap: "4px" }}>
              <Typography sx={styles.dropZoneTextHighlighted}>{t("clickToUpload")}</Typography> {t("orDragAndDrop")}
            </Typography>
            <Typography sx={styles.dropZoneText}>{t("mp4MovAviWebm")}</Typography>
          </Box>
        </Box>
      )}

      {uploadError && (
        <Typography color="error" variant="caption">
          {uploadError.message}
        </Typography>
      )}

      {error && (
        <Typography color="error" variant="caption">
          {error}
        </Typography>
      )}

      {(!!uploadingMedia.length || !!mediaArray.length) && (
        <Box sx={styles.previewContainer}>
          {mediaArray.map((item: Media, index: number) => (
            <MediaPreview
              key={item.url}
              item={item}
              onRemove={() => {
                if (onRemove) {
                  onRemove(item);
                }
                const newMedia = [...mediaArray];
                newMedia.splice(index, 1);
                onChange(multiple ? newMedia : newMedia[0] || null);
              }}
            />
          ))}

          {uploadingMedia.map((item) => (
            <UploadingMediaPreview
              key={item.fileName}
              item={item}
              onRemove={() => setUploadingMedia((prev) => prev.filter((m) => m.fileName !== item.fileName))}
            />
          ))}
        </Box>
      )}
    </FormControl>
  );
};

interface VideoMetadata {
  thumbnail: File;
  duration: number;
}

const getVideoMetadata = async (file: File): Promise<VideoMetadata> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    video.autoplay = true;
    video.muted = true;
    video.src = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      const duration = Math.round(video.duration / 60); // Convert to minutes and round
      video.currentTime = 1; // Set to 1 second for thumbnail
    };

    video.ontimeupdate = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d")?.drawImage(video, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          const thumbnailFile = new File([blob], `${file.name}-thumbnail.jpg`, { type: "image/jpeg" });
          URL.revokeObjectURL(video.src);
          resolve({
            thumbnail: thumbnailFile,
            duration: Math.round(video.duration / 60), // Convert to minutes and round
          });
        } else {
          reject(new Error("Failed to create thumbnail"));
        }
      }, "image/jpeg");
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error("Error loading video"));
    };
  });
};

const styles = {
  dropZone: {
    borderRadius: "12px",
    border: "1px solid var(--Gray---200, #CFCFCF)",
    background: "var(--Base-White, #FFF)",
    padding: "16px 24px",
    textAlign: "center",
    cursor: "pointer",
    display: "flex",
    gap: "12px",
  },
  previewContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    height: "fit-content",
    // minHeight: "200px",
  },
  previewItem: {
    position: "relative",
    width: "100%",
    height: "200px",
    borderRadius: "4px",
    overflow: "hidden",
  },
  uploadingContainer: {
    position: "relative",
    width: "100%",
    height: "fit-content",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Base-White, #FFF)",
  },
  fileTypeContainer: {
    position: "relative",
    width: "33px",
    ml: "7px",
    height: "40px",
  },
  fileNameContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  fileName: {
    fontFamily: "Montserrat",
    overflow: "hidden",
    color: "var(--Gray-700, #344054)",
    textOverflow: "ellipsis",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  fileSize: {
    fontFamily: "Montserrat",
    overflow: "hidden",
    color: "var(--Gray-600, #475467)",
    textOverflow: "ellipsis",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  fileType: {
    position: "absolute",
    bottom: "6px",
    right: "9px",
    fontFamily: "Montserrat",
    color: "var(--Base-White, #FFF)",
    textAlign: "center",
    fontSize: "10px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    padding: "2px 3px",
    borderRadius: "2px",
    background: "var(--Blue-dark-600, #155EEF)",
  },

  media: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  removeButton: {
    position: "absolute",
    top: 2,
    right: 2,
    padding: "2px",
    borderRadius: "8px",
  },
  dropZoneText: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    textAlign: "left",
  },
  dropZoneTextHighlighted: {
    color: "var(--Brand-700, #076D0D)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
};
